import { useState } from 'react';
import { Link } from 'react-router';
import Icon from './Icon';
import { grams, kcal } from '../lib/format';
import { addDays, longDate, shortDate, today } from '../lib/dates';
import { useAppPath } from '../lib/AppContext';

// Targets change from a date on and never rewrite the past: each day is judged against the targets in force
// that day. These are the places that say so.

// "2.200 kcal · 140 g đạm"
export const targetsText = (t) => [
  t.calorie_goal ? `${kcal(t.calorie_goal)} kcal` : null,
  t.protein_goal ? `${grams(t.protein_goal)} đạm` : null,
].filter(Boolean).join(' · ') || 'chưa đặt';

// Changing targets once today is under way asks whether they start today or tomorrow. The rule is the
// server's (UserTargets#day_started?, sent as the profile's `today.started`).
export const askFrom = (info) => !!info?.started;

// "Áp dụng mục tiêu mới từ khi nào?": old → new, then "Từ hôm nay" (default) or "Từ ngày mai".
// `info` is the profile's `today` (what was logged and the targets in force); `next` the new targets.
export function ApplyFromChoice({ info, next, value, onChange }) {
  const left = next.calorie_goal ? next.calorie_goal - info.calories_in + info.calories_out : null;
  const options = [
    {
      id: 'today', title: `Từ hôm nay, ${shortDate(info.date)}`,
      text: left == null ? 'Tiến độ hôm nay tính theo mục tiêu mới'
        : `Tiến độ hôm nay tính theo ${kcal(next.calorie_goal)} kcal (${left < 0 ? 'vượt' : 'còn'} ${kcal(Math.abs(left))} kcal)`,
    },
    {
      id: 'tomorrow', title: `Từ ngày mai, ${shortDate(addDays(info.date, 1))}`,
      text: info.targets.calorie_goal ? `Hôm nay giữ mục tiêu cũ ${kcal(info.targets.calorie_goal)} kcal` : 'Hôm nay giữ mục tiêu cũ',
    },
  ];
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-muted">
        Hôm nay bạn đã ghi {kcal(info.calories_in)} kcal. Chọn ngày bắt đầu để tiến độ hôm nay không bị lệch.
      </p>
      <div className="tabular flex items-center justify-between gap-2 rounded-[14px] bg-base-200 px-4 py-3 text-sm">
        <span className="text-muted">{targetsText(info.targets)}</span>
        <Icon name="chevronRight" className="size-4 shrink-0 text-muted" />
        <b>{targetsText(next)}</b>
      </div>
      <div role="radiogroup" aria-label="Áp dụng từ" className="flex flex-col gap-2">
        {options.map((o) => (
          <button key={o.id} type="button" role="radio" aria-checked={value === o.id} onClick={() => onChange(o.id)}
            className={`flex min-h-14 items-center gap-3 rounded-[16px] border-2 px-4 py-2 text-left ${value === o.id ? 'border-primary bg-[#e3f1e9]/50' : 'border-line'}`}>
            <span className={`grid size-5 shrink-0 place-items-center rounded-full border-2 ${value === o.id ? 'border-primary' : 'border-base-300'}`}>
              {value === o.id && <span className="size-2.5 rounded-full bg-primary" />}
            </span>
            <span>
              <span className="block font-semibold">{o.title}</span>
              <span className="block text-xs text-muted">{o.text}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

const dismissKey = (from) => `macro-mate:targets-card:${from}`;
const isDismissed = (from) => {
  try {
    return window.localStorage.getItem(dismissKey(from)) === '1';
  } catch {
    return false;
  }
};

// "Mục tiêu mới từ hôm nay" on Today for the first two days of new targets, until closed.
export function NewTargetsCard({ change }) {
  const path = useAppPath();
  const [hidden, setHidden] = useState(() => isDismissed(change.effective_from));
  if (hidden) return null;
  const close = () => {
    try {
      window.localStorage.setItem(dismissKey(change.effective_from), '1');
    } catch {
      // Private browsing: it hides for now and goes away by itself after two days anyway.
    }
    setHidden(true);
  };
  const started = change.effective_from === today() ? 'từ hôm nay' : `từ ${longDate(change.effective_from)}`;
  return (
    <section className="flex items-start gap-3 rounded-[20px] bg-[#e3f1e9] p-4 text-[#17201b]">
      <span className="grid size-10 shrink-0 place-items-center rounded-full bg-[#17804f] text-white">
        <Icon name="target" className="size-5" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <h2 className="text-[15px] font-bold text-[#17804f]">Mục tiêu mới {started}</h2>
        <p className="tabular text-sm font-semibold">{targetsText(change.current)}</p>
        <p className="tabular text-xs text-[#4a5a51]">Trước đó: {targetsText(change.previous)}</p>
        <Link to={path('/targets')} className="mt-1 w-fit text-sm font-semibold text-[#17804f] underline">Xem lịch sử mục tiêu</Link>
      </div>
      <button type="button" className="btn btn-circle btn-ghost btn-sm -mr-1 -mt-1 size-9" aria-label="Ẩn thông báo" onClick={close}>
        <Icon name="x" className="size-4" />
      </button>
    </section>
  );
}

// Under a past day's calorie ring: the targets it is measured against, flagged "Mục tiêu cũ" when they
// differ from today's. Opens the targets history.
export function DayTargetsNote({ day }) {
  const path = useAppPath();
  return (
    <Link to={path('/targets')} className="mt-4 flex items-center gap-2 rounded-[14px] bg-base-200 px-3 py-2.5 text-sm">
      <Icon name="clock" className="size-4 shrink-0 text-muted" />
      <span className="tabular min-w-0 flex-1">
        Mục tiêu ngày này: <b>{targetsText(day)}</b>
      </span>
      {day.old_targets && (
        <span className="shrink-0 rounded-full bg-[#fdebe4] px-2 py-0.5 text-xs font-semibold text-[#a4471b]">Mục tiêu cũ</span>
      )}
    </Link>
  );
}
